import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import SmartImage from "./SmartImage";
import { enquireHref, money } from "../lib/format";
import { useCart } from "../lib/cart";
import { useCheckoutEnabled } from "../lib/checkout";

/** Shop product with a real price - can be added to cart. */
export default function ProductCard({ product }) {
  const { add } = useCart();
  const payments = useCheckoutEnabled();
  const href = `/shop/${product.id || product.slug}`;
  const onHand = Number(product.qty ?? 0);
  const inStock = onHand > 0;

  return (
    <article className="card-nq group flex flex-col" data-testid="product-card">
      <Link to={href} className="relative block overflow-hidden">
        <SmartImage
          src={product.image}
          alt={product.name}
          ratio="aspect-square"
          fit="contain"
          fallbackLabel={product.name}
          className="transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <span
          className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] shadow"
          style={{ background: "var(--nq-gold)", color: "var(--nq-black)" }}
          data-testid="cost-price-badge"
        >
          Cost price
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            style={{
              background: inStock ? "rgba(31,107,58,0.3)" : "rgba(200,60,50,0.28)",
              color: inStock ? "var(--nq-sand)" : "#ffd9d5",
            }}
            data-testid="product-card-stock"
          >
            {inStock ? (onHand <= 3 ? `Only ${onHand} left` : "In stock") : "Sold out"}
          </span>
          {product.sku && (
            <span className="font-mono text-[10px] text-[color:var(--nq-stone)]">{product.sku}</span>
          )}
        </div>

        <h3 className="text-sm font-semibold leading-snug text-[color:var(--nq-sand)]">
          <Link to={href} className="transition hover:text-[color:var(--nq-gold)]">
            {product.name}
          </Link>
        </h3>

        <p className="mt-2 flex-1 font-mono text-lg font-bold gold-text" data-testid="product-price">
          {money(product.price)}
        </p>

        <div className="mt-3 flex gap-2">
          {payments ? (
            <button
              type="button"
              disabled={!inStock}
              onClick={() => {
                add({
                  slug: product.id || product.slug,
                  name: product.name,
                  price: product.price,
                  image: product.image,
                });
                toast.success("Added to cart", { description: product.name });
              }}
              className="btn-green min-h-[44px] flex-1 px-3 py-2.5 text-xs disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:py-2"
              data-testid="product-card-primary-cta"
            >
              <ShoppingCart className="h-3.5 w-3.5" /> {inStock ? "Add to cart" : "Sold out"}
            </button>
          ) : (
            <Link
              to={inStock ? enquireHref({ type: "product", id: product.id || product.slug, title: product.name, image: product.image, price: money(product.price) }) : href}
              className="btn-green min-h-[44px] flex-1 px-3 py-2.5 text-xs sm:min-h-0 sm:py-2"
              data-testid="product-card-primary-cta"
            >
              <MessageSquare className="h-3.5 w-3.5" /> {inStock ? "Enquire to buy" : "Sold out"}
            </Link>
          )}
          <Link to={href} className="btn-outline-nq min-h-[44px] px-3 py-2.5 text-xs sm:min-h-0 sm:py-2" data-testid="product-card-secondary-cta">
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}
